/**
 * Section 1 — Scope (C1–C6 + C6.1 carve-out applicability).
 *
 * Each C1–C6 tile renders as a Cloudscape ExpandableSection (collapsed by
 * default) showing the rationale, the PDF page link, and the decision status.
 * A single "Edit" button per tile opens EditCheckModal, which edits the
 * rationale, the AI-extracted fields, AND the decision in one place — before
 * or after a decision (#86). C6 carve-outs are decided per-row in CarveoutsList.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ExpandableSection,
  SpaceBetween,
  Box,
  Button,
  ColumnLayout,
  Badge,
  Spinner,
  Textarea,
} from "@cloudscape-design/components";
import type { ActionItem, ScopeCheck } from "@/core/api/types";
import { CommentThread } from "@/core/ui/CommentThread";
import { PdfPageLink } from "@/core/ui/PdfPageLink";
import { SectionStatusBadge } from "@/core/ui/SectionStatusBadge";
import { SubcardHeader } from "@/core/ui/SubcardHeader";
import type { Focus } from "@/shell/ReportDetail";
import { CarveoutsList } from "./CarveoutsList";
import { EditCheckModal, decisionOptionsFor, type DecisionOpt } from "./EditCheckModal";
import { updateScopeCheck } from "./api";

interface Props {
  reportId: string;
  scopeChecks: ScopeCheck[];
  onUpdated: () => void;
  openCount?: number;
  /** #119: the report's OPEN action items (derive-on-read filtered) — the single
   *  source of truth for per-check / per-carve-out Open|Done. Cards read their
   *  state from here so the section badge can never drift from the cards. */
  openItems?: ActionItem[];
  pdfPage?: number | null;
  focus?: Focus | null;
  /** #C7: the Bridge Letter rendered as the 7th Scope card (only when C2 =
   *  Partial). Composed by the shell and slotted in here so this section never
   *  imports another section (eslint boundary). `bridgeFocused` expands the
   *  Scope card when an inbox link targets the bridge. */
  bridgeCard?: ReactNode;
  bridgeFocused?: boolean;
  /** #C6: navigate to a C6 carve-out's linked/placeholder SOC 1 report. */
  onOpenReport?: (reportId: string) => void;
}

const CHECK_TITLES: Record<number, string> = {
  1: "C1 — Service scope correct?",
  2: "C2 — Period covers FY?",
  3: "C3 — SOC 1 Type II?",
  4: "C4 — Auditor AICPA + reputable?",
  5: "C5 — Opinion modification?",
  6: "C6 — Subservice organizations?",
};

function CheckTile({
  check,
  reportId,
  onUpdated,
  focusTargetId,
  openTargets,
  onOpenReport,
}: {
  check: ScopeCheck;
  reportId: string;
  onUpdated: () => void;
  focusTargetId?: string | null;
  /** target_ids of the report's OPEN scope action items (#119). */
  openTargets: Set<string>;
  /** #C6: navigate to a carve-out's linked/placeholder SOC 1 report. */
  onOpenReport?: (reportId: string) => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  // #119: one-click decision buttons, shown on the card while the check is Open
  // so management can decide without opening the Edit modal. `deciding` tracks
  // which option is in flight to disable the row and spin only that button.
  const [deciding, setDeciding] = useState<string | null>(null);
  // #119: while Open, the summary is edited inline via a pencil (no decision
  // field) — the decision lives in the buttons below. Once Done, the full Edit
  // modal takes over both.
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [savingSummary, setSavingSummary] = useState(false);
  const overrides = (check.user_overrides ?? {}) as Record<string, unknown>;
  const containerRef = useRef<HTMLDivElement>(null);
  const isFocused = focusTargetId === check.id;
  const decided = !!check.user_decision;
  // #119: the flat terminal decision list (also used by the Edit modal).
  const decisionOpts = decisionOptionsFor(check);
  // #119: Open|Done is read from the action items (single source of truth) — a
  // C1–C5 card is Open iff an open scope item targets it. A *decided* check can
  // still be Open: a failing answer (e.g. C3 "not Type II") leaves "Get new SOC
  // 1" outstanding. So the BADGE follows `isOpen`, while the body below switches
  // on `decided` (undecided → decision buttons; decided → read-only + Edit). C6
  // counts its carve-outs separately (c6OpenCount).
  const isOpen = openTargets.has(check.id);
  const isDone = check.check_num !== 6 && !isOpen;
  const [expanded, setExpanded] = useState(isFocused || !isDone);
  const displayRationale = (overrides.user_rationale as string | undefined) ?? check.ai_rationale ?? "";
  // Per-check badge (#45 calibration): a binary check (C1–C5) is simply Open
  // or Done — only C6 (carve-outs) has multiple sub-items worth counting.
  // #99: C6 is Done when every declared subservice org is decided, Open while
  // any isn't. Count from the carve-out decisions/coverage — NOT countForCheck,
  // whose scope_check filter is always 0 for C6 (carve-out items are "carveout").
  const c6OpenCount = (() => {
    if (check.check_num !== 6) return 0;
    // #119: count carve-outs that own an OPEN action item — i.e. derive-on-read
    // says work remains (undecided or request-SOC-pending). Reads the same set
    // the section badge counts, so C6's number can't drift from it.
    type Co = { id?: string; name?: string };
    const ai = ((check.ai_data as Record<string, unknown> | null)?.carveouts as Co[] | undefined) ?? [];
    const manual = (overrides.carveouts_list ?? []) as Co[];
    const keyOf = (c: Co) => c.id ?? c.name ?? "(unnamed)";
    return [...ai, ...manual].filter((c) => openTargets.has(keyOf(c))).length;
  })();
  const finalBadge = (() => {
    if (check.check_num === 6) {
      if (c6OpenCount > 0) return { color: "red" as const, text: `${c6OpenCount} open` };
      return { color: "green" as const, text: "Done" };
    }
    return isDone
      ? { color: "green" as const, text: "Done" }
      : { color: "red" as const, text: "Open" };
  })();
  // Per-check PDF page (#89): AIP-1 sets ai_data.page per check (C1 → system
  // description / Section III; C2-C6 → auditor's opinion / Section I).
  const aiData = check.ai_data as Record<string, unknown> | null;
  const aiPage = (aiData?.page ?? aiData?.system_description_start_page) as number | undefined;

  // #119: a short verdict tag (visible when collapsed) reading the tailored
  // decision answer. Shown only once Done. Pre-#119 values are aliased so older
  // dev reports don't read blank.
  const verdictTag = (() => {
    const d = (check.user_decision ?? "").toLowerCase();
    switch (check.check_num) {
      case 1:
        return d === "incorrect" || d === "get_new_soc1" ? "Needs new SOC 1" : "Correct";
      case 2:
        if (d === "partial") return "Partial — Bridge letter";
        if (d === "wrong_period") return "Wrong year — get new SOC 1";
        if (d === "none") return "No overlap — get new SOC 1";
        // full / legacy "accepted": covers the FY.
        return "Covers FY";
      case 3:
        return d === "not" || d === "get_new_soc1" ? "Not Type II — get new SOC 1" : "SOC 1 Type II";
      case 4:
        return d === "reject" || d === "auditor_reject" ? "Reject — get new SOC 1" : "Reputable";
      case 5:
        if (d === "rely") return "Modified — rely";
        if (d === "non_reliance") return "Modified — non-reliance";
        return "Unmodified";
      default:
        return null; // C6 carries its own per-carve-out signals
    }
  })();
  void aiData;

  // #119: inline decision buttons for the Open card. C5 is a guided two-step:
  // step 1 asks Unmodified/Modified; clicking "Modified" sets the c5_modified
  // flag (via setsFlag) and step 2 asks Rely/Non-reliance. Other checks use the
  // flat list straight.
  const c5Modified = overrides.c5_modified === true;
  const inlineOpts: DecisionOpt[] =
    check.check_num === 5
      ? c5Modified
        ? [
            { value: "rely", label: "Rely — document the impact", reason: true },
            { value: "non_reliance", label: "Non-reliance — document", reason: true },
          ]
        : [
            { value: "unmodified", label: "Unmodified" },
            { value: "modified", label: "Modified", setsFlag: "c5_modified" },
          ]
      : decisionOpts;

  async function decide(opt: DecisionOpt) {
    setDeciding(opt.value);
    try {
      if (opt.setsFlag) {
        // Guided step (C5 "Modified") — flips a UI sub-state, not a decision.
        await updateScopeCheck(check.id, { user_overrides: { ...overrides, [opt.setsFlag]: true } });
      } else {
        await updateScopeCheck(check.id, {
          user_decision: opt.value,
          // A route branch carries an optional reason; the one-click path leaves
          // it null — refine later via Edit or a comment.
          ...(opt.reason ? { decline_reason: null } : {}),
        });
      }
      onUpdated();
    } finally {
      setDeciding(null);
    }
  }

  // #119: C5 step-2 escape — "not modified after all" clears the flag → step 1.
  async function clearC5Modified() {
    await updateScopeCheck(check.id, { user_overrides: { ...overrides, c5_modified: false } });
    onUpdated();
  }

  function openSummaryEdit() {
    setSummaryDraft(displayRationale);
    setEditingSummary(true);
  }
  async function saveSummary() {
    if (summaryDraft === displayRationale) {
      setEditingSummary(false);
      return;
    }
    setSavingSummary(true);
    try {
      await updateScopeCheck(check.id, { user_overrides: { ...overrides, user_rationale: summaryDraft } });
      onUpdated();
      setEditingSummary(false);
    } finally {
      setSavingSummary(false);
    }
  }

  // #109: collapse once the check becomes Done (e.g. a decision saved via the
  // Edit modal). Runs only on the transition, so a Done card the user re-expands
  // to review stays open.
  useEffect(() => {
    if (isDone) setExpanded(false);
  }, [isDone]);

  useEffect(() => {
    if (isFocused && containerRef.current) {
      setExpanded(true);
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isFocused]);

  return (
    <div ref={containerRef} className={isFocused ? "focus-flash" : undefined}>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        header={
          <SubcardHeader
            title={CHECK_TITLES[check.check_num] ?? `Check ${check.check_num}`}
            actions={
              <>
                {/* #119: the verdict tag shows once a decision exists — including
                    a failing one that's still Open (e.g. "Not Type II — get new
                    SOC 1"), so the card reads its outcome while badged Open. */}
                {decided && verdictTag && <Badge color="blue">{verdictTag}</Badge>}
                <Badge color={finalBadge.color}>{finalBadge.text}</Badge>
                {typeof aiPage === "number" && <PdfPageLink reportId={reportId} page={aiPage} />}
              </>
            }
          />
        }
      >
        <SpaceBetween size="s">
          {/* C6 shows NO section summary text. CarveoutsList renders the
              subservice-org cards when any exist (AI-found or manually added),
              or a deterministic "No subservice organization in the report" line
              when there are zero — it follows the live row count, so the text
              auto-updates as the user adds/removes orgs. C1–C5 render their
              summary inside the branch below so the inline pencil can attach. */}
          {/* #108: the AI's original is no longer shown as a persistent block —
              an edit posts it into the Comments thread instead. */}

          {check.check_num === 6 ? (
            // #99: C6 has no section-level Edit — each subservice-org card has its
            // own Edit pencil (in CarveoutsList).
            <CarveoutsList
              check={check}
              reportId={reportId}
              onUpdated={onUpdated}
              openTargets={openTargets}
              onOpenReport={onOpenReport}
            />
          ) : (
            // #119: while Open (no decision yet), edit the summary inline via a
            // pencil and decide with the buttons. Once Done — by AI or human —
            // the summary goes read-only and the Edit button opens the full
            // popup (summary + decision).
            <SpaceBetween size="s">
              {editingSummary ? (
                <SpaceBetween size="xs">
                  <Textarea
                    value={summaryDraft}
                    onChange={({ detail }) => setSummaryDraft(detail.value)}
                    rows={5}
                  />
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => setEditingSummary(false)} disabled={savingSummary}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={saveSummary} loading={savingSummary} disabled={savingSummary}>
                      Save
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              ) : (
                <Box>
                  {displayRationale}
                  {!decided && (
                    <Button
                      variant="inline-icon"
                      iconName="edit"
                      ariaLabel="Edit summary"
                      onClick={openSummaryEdit}
                    />
                  )}
                </Box>
              )}

              {/* #119: the inline decision buttons show while UNDECIDED. Once a
                  decision exists — passing OR failing — the full Edit modal owns
                  changes (a failing card stays badged Open with its verdict). */}
              {!decided && inlineOpts.length > 0 && !editingSummary && (
                <>
                  {check.check_num === 5 && c5Modified && (
                    <Box color="text-status-info" fontSize="body-s">
                      Modified opinion — document the impact, then decide:
                    </Box>
                  )}
                  <SpaceBetween direction="horizontal" size="xs">
                    {inlineOpts.map((opt, i) => (
                      <Button
                        key={opt.value}
                        variant={i === 0 ? "primary" : "normal"}
                        loading={deciding === opt.value}
                        disabled={deciding !== null}
                        onClick={() => decide(opt)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                    {check.check_num === 5 && c5Modified && (
                      <Button variant="link" onClick={clearC5Modified} disabled={deciding !== null}>
                        ← Not modified
                      </Button>
                    )}
                  </SpaceBetween>
                </>
              )}

              {decided && (
                <Box>
                  <Button variant="normal" iconName="edit" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                </Box>
              )}
            </SpaceBetween>
          )}

          {/* C6 surfaces the section-level Comments via CarveoutsList's macro
              3-button row (#99) — avoid a duplicate thread on the same target. */}
          {check.check_num !== 6 && (
            <Box>
              <Box variant="awsui-key-label">Comments</Box>
              <CommentThread kind="scope-check" targetId={check.id} onChange={onUpdated} />
            </Box>
          )}
        </SpaceBetween>

        <EditCheckModal
          check={check}
          visible={editOpen}
          onDismiss={() => setEditOpen(false)}
          onSaved={onUpdated}
        />
      </ExpandableSection>
    </div>
  );
}

export function ScopeSection({
  reportId,
  scopeChecks,
  onUpdated,
  openCount = 0,
  openItems = [],
  pdfPage,
  focus,
  bridgeCard,
  bridgeFocused = false,
  onOpenReport,
}: Props) {
  const sorted = useMemo(() => [...scopeChecks].sort((a, b) => a.check_num - b.check_num), [scopeChecks]);
  // #119: target_ids of this report's OPEN scope action items. Every card reads
  // its Open|Done from this one set, so the section badge (= openCount, also the
  // open scope items) can never disagree with the cards.
  const openTargets = useMemo(
    () =>
      new Set(
        openItems.filter((i) => i.section === "scope" && i.target_id).map((i) => i.target_id as string),
      ),
    [openItems],
  );
  const processing = !scopeChecks.length;
  // Per-check PDF page links live inside each C1-C6 card body (not in the
  // Scope section header), so we ignore the section-level pdfPage prop here.
  void pdfPage;
  const badge = <SectionStatusBadge processing={processing} openCount={openCount} />;
  // Expand on a scope focus OR a bridge focus (C7 lives here now).
  const defaultExpanded = (!!focus && focus.section === "scope") || bridgeFocused;

  if (processing)
    return (
      <ExpandableSection
        variant="container"
        defaultExpanded={defaultExpanded}
        headerText="Scope"
        headerActions={badge}
      >
        <Box textAlign="center" padding="l">
          <Spinner /> Waiting for ingest to produce scope checks…
        </Box>
      </ExpandableSection>
    );

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded={defaultExpanded}
      headerText="Scope"
      headerActions={badge}
    >
      <ColumnLayout columns={1}>
        {sorted.map((c) => (
          <CheckTile
            key={c.id}
            check={c}
            reportId={reportId}
            onUpdated={onUpdated}
            focusTargetId={focus?.targetId ?? null}
            openTargets={openTargets}
            onOpenReport={onOpenReport}
          />
        ))}
        {/* #C7: the Bridge Letter card (only when C2 = Partial) — composed by the
            shell and slotted here as the 7th card so it sits with C1–C6. */}
        {bridgeCard}
      </ColumnLayout>
    </ExpandableSection>
  );
}
